/**
 * Authentication hook
 * Provides authentication state and methods
 * Now uses Redux for state management
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { useUser } from '@/store/hooks';
import {
  loadUser,
  loginUser,
  registerUser,
  logoutUser,
  refreshUser,
  sendOperatorOTP,
  verifyOperatorOTP,
  authenticateWithGoogle,
} from '@/store/slices/userSlice';
import { apiClient } from '@/lib/api/client';
import { useDummyMode } from './useDummyMode';
import type { User } from '@/store/slices/userSlice';

interface UseAuthReturn {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: any }>;
  register: (data: any) => Promise<{ success: boolean; error?: any }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  sendOperatorOTP: (email: string, type: 'LOGIN' | 'SIGNUP') => Promise<{ success: boolean; error?: any }>;
  verifyOperatorOTP: (email: string, code: string, type: 'LOGIN' | 'SIGNUP', name?: string) => Promise<{ success: boolean; error?: any }>;
  completeOperatorProfile: (
    email: string,
    payload: {
      name: string;
      phone: string;
      address: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postalCode: string;
      };
    }
  ) => Promise<{ success: boolean; error?: any }>;
  sendTouristOTP: (email: string, type?: 'LOGIN' | 'SIGNUP') => Promise<{ success: boolean; error?: any }>;
  loginTourist: (email: string, code: string, type?: 'LOGIN' | 'SIGNUP') => Promise<{ success: boolean; error?: any }>;
  authenticateWithGoogle: (
    role: 'tourist' | 'operator',
    credential: string
  ) => Promise<{ success: boolean; error?: any }>;
}

// Export User type for backward compatibility
export type { User };

// Module-level flag to prevent multiple initializations across remounts
let hasInitialized = false;

export function useAuth(): UseAuthReturn {
  const dispatch = useAppDispatch();
  const { user, isLoading, isAuthenticated, error, isLoadingUser } = useUser();
  const { isDummyMode } = useDummyMode();

  /**
   * Initialize auth state on mount
   * Only runs once per app session to prevent infinite loops
   */
  useEffect(() => {
    // Prevent multiple initializations using module-level flag
    if (hasInitialized) {
      return;
    }
    
    // If user is already authenticated, mark as initialized and skip
    if (isAuthenticated && user) {
      hasInitialized = true;
      return;
    }
    
    hasInitialized = true;
    
    // Wait for Redux Persist to rehydrate before checking
    // This prevents calling loadUser before persisted state is available
    const checkAuth = () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (token) {
        // Only load user if not already authenticated and not currently loading
        if (!isAuthenticated && !isLoadingUser) {
          dispatch(loadUser());
        }
      } else {
        // No token - if user exists in state, clear it
        // This handles edge cases where persisted user exists but token is gone
        if (user) {
          dispatch(logoutUser());
        }
      }
    };

    // Small delay to ensure Redux Persist has rehydrated
    // Also allows multiple components to mount before we check
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [dispatch, isAuthenticated, user, isLoadingUser]); // Include isAuthenticated and user to check current state

  /**
   * Login user
   */
  const login = useCallback(
    async (email: string, password: string) => {
      const result = await dispatch(loginUser({ email, password }));
      if (loginUser.fulfilled.match(result)) {
        return { success: true };
      }
      return {
        success: false,
        error: result.payload || {
          code: 'LOGIN_ERROR',
          message: 'Login failed',
        },
      };
    },
    [dispatch]
  );

  /**
   * Register user
   */
  const register = useCallback(
    async (data: any) => {
      const result = await dispatch(registerUser(data));
      if (registerUser.fulfilled.match(result)) {
        return { success: true };
      }
      return {
        success: false,
        error: result.payload || {
          code: 'REGISTER_ERROR',
          message: 'Registration failed',
        },
      };
    },
    [dispatch]
  );

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    await dispatch(logoutUser());
  }, [dispatch]);

  /**
   * Refresh user data
   */
  const refreshUserCallback = useCallback(async () => {
    await dispatch(refreshUser());
  }, [dispatch]);

  /**
   * Send OTP to operator email
   */
  const sendOperatorOTPCallback = useCallback(
    async (email: string, type: 'LOGIN' | 'SIGNUP') => {
      const result = await dispatch(sendOperatorOTP({ email, type, isDummyMode }));
      if (sendOperatorOTP.fulfilled.match(result)) {
        return { success: true };
      }
      return {
        success: false,
        error: result.payload || {
          code: 'OTP_SEND_ERROR',
          message: 'Failed to send OTP',
        },
      };
    },
    [dispatch, isDummyMode]
  );

  /**
   * Verify OTP and login/signup operator
   */
  const verifyOperatorOTPCallback = useCallback(
    async (email: string, code: string, type: 'LOGIN' | 'SIGNUP', name?: string) => {
      const result = await dispatch(
        verifyOperatorOTP({
          email,
          code,
          type,
          name,
          isDummyMode,
        })
      );
      if (verifyOperatorOTP.fulfilled.match(result)) {
        return { success: true };
      }
      return {
        success: false,
        error: result.payload || {
          code: 'OTP_VERIFY_ERROR',
          message: 'Failed to verify OTP',
        },
      };
    },
    [dispatch, isDummyMode]
  );

  /**
   * Complete operator profile (for authenticated users)
   */
  const completeOperatorProfileCallback = useCallback(
    async (
      email: string,
      payload: {
        name: string;
        phone: string;
        address: {
          line1: string;
          line2?: string;
          city: string;
          state: string;
          postalCode: string;
        };
      }
    ) => {
      const { authApi } = await import('@/lib/api/auth.api');
      const response = await authApi.completeOperatorProfile(email, payload);
      if (response.success && response.data) {
        // Reload user data to get updated profile
        await dispatch(refreshUser());
        return { success: true };
      }
      return {
        success: false,
        error: response.error || {
          code: 'PROFILE_UPDATE_ERROR',
          message: 'Failed to update profile',
        },
      };
    },
    [dispatch]
  );

  /**
   * Send OTP to tourist email
   */
  const sendTouristOTPCallback = useCallback(
    async (email: string, type: 'LOGIN' | 'SIGNUP' = 'LOGIN') => {
      const { authApi } = await import('@/lib/api/auth.api');
      const response = await authApi.sendTouristOTP(email, isDummyMode, type);
      if (response.success) {
        return { success: true };
      }
      return {
        success: false,
        error: response.error || {
          code: 'OTP_SEND_ERROR',
          message: 'Failed to send OTP',
        },
      };
    },
    [isDummyMode]
  );

  /**
   * Verify OTP and login/create tourist user
   */
  const loginTouristCallback = useCallback(
    async (email: string, code: string, type: 'LOGIN' | 'SIGNUP' = 'LOGIN') => {
      const { authApi } = await import('@/lib/api/auth.api');
      const response = await authApi.loginTourist(email, code, isDummyMode, type);
      if (response.success && response.data) {
        // Load user data after successful login
        await dispatch(loadUser());
        return { success: true };
      }
      return {
        success: false,
        error: response.error || {
          code: 'OTP_VERIFY_ERROR',
          message: 'Failed to verify OTP',
        },
      };
    },
    [dispatch, isDummyMode]
  );

  const authenticateWithGoogleCallback = useCallback(
    async (role: 'tourist' | 'operator', credential: string) => {
      const result = await dispatch(authenticateWithGoogle({ role, credential }));
      if (authenticateWithGoogle.fulfilled.match(result)) {
        return { success: true };
      }
      return {
        success: false,
        error: result.payload || {
          code: 'GOOGLE_AUTH_ERROR',
          message: 'Failed to authenticate with Google',
        },
      };
    },
    [dispatch]
  );

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshUser: refreshUserCallback,
    sendOperatorOTP: sendOperatorOTPCallback,
    verifyOperatorOTP: verifyOperatorOTPCallback,
    completeOperatorProfile: completeOperatorProfileCallback,
    sendTouristOTP: sendTouristOTPCallback,
    loginTourist: loginTouristCallback,
    authenticateWithGoogle: authenticateWithGoogleCallback,
  };
}
